/**
 * TeamsDialog - change the side of one or several combatants on the roster.
 *
 * Pick people in the list (a click toggles a row), then press where they
 * go: a team already on the roster, no team, or a new team named here. A
 * team is a fact on the SHEET (`engine/roster/team`), so each move is a
 * sheet edit like any other - the host offers this dialog only to someone
 * who may edit sheets, and the move shows in the sheet's Changed list.
 *
 * The dialog stays open after a move: the list regroups under your eyes,
 * which is the confirmation.
 *
 * TWO TEAMS ON ONE TAG (the M6 merge, 2026-10-23) are said here, never fixed
 * silently: Split re-tags the smaller titles, each on the old tag's side so
 * allies stay allies, and Join them again puts every record back exactly
 * (sheets sit outside undo, so the dialog keeps the way back). Each team's
 * SIDE is picked here too, for all its members at once.
 *
 * A kill-team's setup is not here (plan modes-rework): it is on the sheets, set in the character editor's Team tab
 * and the Assets page's Teams view. A move here takes the team's record whole, kill-team block included.
 */
import { useMemo, useState } from 'react';
import common from '../styles/common.module.css';
import styles from '../styles/TeamsDialog.module.css';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';
import {
  liveryByCombatant,
  sidePlan,
  sidesOfTeams,
  groupsLine,
  splitPlan,
  tagConflicts,
  teamInfoOf,
  teamOf,
  teamTag,
  teamsIn,
} from '../engine/roster/team';
import { liveryStyle } from '../utils/liveryStyle';
import { tagMinter } from '../utils/teamTags';

/** One person: the band is their team's colour. */
function PersonRow({ combatant, livery, picked, onToggle }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={picked}
      className={styles.row}
      style={liveryStyle(livery)}
      data-testid={`teams-row-${combatant.id}`}
      onClick={onToggle}
    >
      <span className={styles.name}>{combatant.name}</span>
      <span className={styles.team}>{teamOf(combatant)?.label ?? 'no team'}</span>
    </button>
  );
}

/** A new team: named here, tagged by the host's clock (engine code mints no randomness). */
function NewTeam({ disabled, onCreate }) {
  const [name, setName] = useState('');
  const create = () => {
    onCreate({ tag: teamTag(String(Date.now())), label: name.trim() });
    setName('');
  };
  return (
    <div className={styles.newTeam}>
      <input
        className={styles.input}
        aria-label="A new team"
        placeholder="A new team"
        data-testid="teams-new-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <button
        type="button"
        className={styles.plate}
        data-testid="teams-to-new"
        disabled={disabled || !name.trim()}
        onClick={create}
      >
        Create
      </button>
    </div>
  );
}

/** Where the picked people go. */
function Destinations({ teams, count, onMove }) {
  const none = count === 0;
  return (
    <div className={styles.destinations}>
      <span className={styles.caption}>{none ? 'Pick someone, then where they go' : `Move ${count} to`}</span>
      <div className={styles.plates}>
        {teams.map((team) => (
          <button
            key={team.tag}
            type="button"
            className={styles.plate}
            disabled={none}
            data-testid={`teams-to-${team.tag}`}
            onClick={() => onMove(teamInfoOf(team))}
          >
            {team.label}
          </button>
        ))}
        <button
          type="button"
          className={styles.plate}
          disabled={none}
          data-testid="teams-to-none"
          onClick={() => onMove(null)}
        >
          No team
        </button>
      </div>
      <NewTeam disabled={none} onCreate={onMove} />
    </div>
  );
}

/** "Kill-team Omega (3) and Servidores (2)": the titles wearing one tag. */

/**
 * Each tag worn under more than one title and colour: Split (the smaller titles get tags of their own, on the
 * tag's side), then Join them again to undo it exactly. `undone` holds each split's records as they were, by tag,
 * until anything else changes in the dialog (the host clears it): after a move it would undo more than the split.
 */
function Conflicts({ roster, onApply, undone, setUndone }) {
  const conflicts = useMemo(() => tagConflicts(roster), [roster]);
  const split = (tag) => {
    const plan = splitPlan(roster, tag, tagMinter());
    const before = plan.map(({ id }) => ({ id, team: teamOf(roster.find((c) => c.id === id)) }));
    onApply(plan);
    setUndone((was) => ({ ...was, [tag]: before }));
  };
  const rejoin = (tag) => {
    onApply(undone[tag]);
    setUndone(({ [tag]: _gone, ...rest }) => rest);
  };
  const splits = Object.keys(undone);
  if (!conflicts.length && !splits.length) return null;
  return (
    <div className={styles.conflicts} role="status">
      {conflicts.map(({ tag, groups }) => (
        <p key={tag} className={styles.conflict} data-testid={`teams-conflict-${tag}`}>
          One team tag is worn by {groupsLine(groups)} - they count as ONE team.{' '}
          <button type="button" className={styles.plate} data-testid={`teams-split-${tag}`} onClick={() => split(tag)}>
            Split
          </button>
        </p>
      ))}
      {splits.map((tag) => (
        <p key={tag} className={styles.conflict}>
          Split, each half on the same side.{' '}
          <button
            type="button"
            className={styles.plate}
            data-testid={`teams-rejoin-${tag}`}
            onClick={() => rejoin(tag)}
          >
            Join them again
          </button>
        </p>
      ))}
    </div>
  );
}

/** "Kill-team Omega + Servitors". */
const sideName = (side) => side.teams.map((t) => t.label).join(' + ');

/** A side this team names that no OTHER team here stands on (its anchor left the roster): shown, never hidden. */
const orphanSide = (team, teams) =>
  team.side !== team.tag && !teams.some((t) => t.tag !== team.tag && t.side === team.side);

/**
 * Each team's side, for every member at once (`sidePlan`: each keeps its own title and colour, and an anchor team
 * carries its side with it). A side no other team stands on here is still shown, as it is.
 */
function TeamSides({ roster, teams, onApply }) {
  if (teams.length < 2) return null;
  const setSide = (team, side) => onApply(sidePlan(roster, team.tag, side));
  return (
    <div className={styles.destinations}>
      <span className={styles.caption}>Who fights beside whom</span>
      {teams.map((team) => (
        <label key={team.tag} className={styles.sideRow}>
          <span className={styles.name}>{team.label}</span>
          <select
            className={styles.input}
            data-testid={`teams-side-${team.tag}`}
            value={team.side === team.tag ? '' : team.side}
            onChange={(e) => setSide(team, e.target.value)}
          >
            <option value="">A side of its own</option>
            {sidesOfTeams(teams.filter((t) => t.tag !== team.tag)).map((s) => (
              <option key={s.side} value={s.side}>
                Beside {sideName(s)}
              </option>
            ))}
            {orphanSide(team, teams) && <option value={team.side}>{`Beside "${team.side}" (no team here)`}</option>}
          </select>
        </label>
      ))}
    </div>
  );
}

/**
 * @param {object} props
 * @param {import('../engine/roster/types').Combatant[]} props.roster
 * @param {(ids: string[], team: {tag: string, label: string, livery?: string} | null) => void} props.onMove
 * @param {(plan: {id: string, team: object | null}[]) => void} props.onApply - each person their own record
 * @param {() => void} props.onClose
 */
export default function TeamsDialog({ roster, onMove, onApply, onClose }) {
  const trapRef = useFocusTrap(true);
  const backdrop = useModalBackdrop(onClose, trapRef);
  useEscapeKey(true, onClose); // Escape cancels, as a click outside does (2026-10-23).
  const [picked, setPicked] = useState(() => new Set());
  const teams = useMemo(() => teamsIn(roster), [roster]);
  const liveries = useMemo(() => liveryByCombatant(roster), [roster]);
  const toggle = (id) =>
    setPicked((was) => {
      const next = new Set(was);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  // Each split's way back, until anything else changes here.
  const [undone, setUndone] = useState({});
  const move = (team) => {
    onMove([...picked], team);
    setPicked(new Set());
    setUndone({});
  };
  const changeSide = (plan) => {
    onApply(plan);
    setUndone({});
  };
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        className={common.modalContent}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Change team"
        data-testid="teams-dialog"
      >
        <h3 className={styles.title}>Change team</h3>
        <div className={styles.list} role="listbox" aria-multiselectable="true" aria-label="The roster">
          {roster.map((c) => (
            <PersonRow
              key={c.id}
              combatant={c}
              livery={liveries[c.id]}
              picked={picked.has(c.id)}
              onToggle={() => toggle(c.id)}
            />
          ))}
        </div>
        <Conflicts roster={roster} onApply={onApply} undone={undone} setUndone={setUndone} />
        <Destinations teams={teams} count={picked.size} onMove={move} />
        <TeamSides roster={roster} teams={teams} onApply={changeSide} />
        <div className={styles.footer}>
          <button type="button" className={common.btnSmall} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
